import type { MultiAreaRow } from "@shared/components/Charts/MultiAreaChart";
import { BigDecimal } from "@solomon-labs/bigdecimal";

export interface ReserveHolding {
    asset: string;
    amount: string;
    amountUsd: string;
}
export interface ReserveSnapshot {
    at: string;
    custody: ReserveHolding[] | null;
    vault: ReserveHolding[] | null;
    yieldDistributors: ReserveHolding[] | null;
    reserveFund: ReserveHolding[] | null;
}
export interface ReserveHistoryPage {
    data: ReserveSnapshot[];
    hasMore: boolean;
}
const GROUPS = ["custody", "vault", "yieldDistributors", "reserveFund"] as const;

/** Reject malformed data instead of turning unknown balances into zero. */
export function decodeReserveHistory(value: unknown): ReserveHistoryPage {
    if (!value || typeof value !== "object") throw new Error("Invalid reserve history");
    const page = value as Record<string, unknown>;
    if (!Array.isArray(page.data) || typeof page.hasMore !== "boolean") throw new Error("Invalid reserve history page");
    for (const entry of page.data) {
        if (!entry || typeof entry !== "object") throw new Error("Invalid reserve snapshot");
        const row = entry as Record<string, unknown>;
        if (typeof row.at !== "string" || !Number.isFinite(Date.parse(row.at)))
            throw new Error("Invalid snapshot date");
        for (const key of GROUPS) {
            const group = row[key];
            if (group === null) continue;
            if (!Array.isArray(group)) throw new Error("Invalid reserve holdings");
            for (const item of group) {
                if (!item || typeof item !== "object") throw new Error("Invalid reserve holding");
                const holding = item as Record<string, unknown>;
                if (
                    typeof holding.asset !== "string" ||
                    !holding.asset.trim() ||
                    typeof holding.amount !== "string" ||
                    !/^\d+(\.\d+)?$/.test(holding.amount) ||
                    typeof holding.amountUsd !== "string" ||
                    !/^\d+(\.\d+)?$/.test(holding.amountUsd) ||
                    !Number.isFinite(Number(holding.amountUsd))
                )
                    throw new Error("Invalid reserve amount");
            }
        }
    }
    return page as unknown as ReserveHistoryPage;
}

/** Sum stored USD values exactly. Convert to numbers only at the chart boundary.
 * An absent asset stays absent, since the feed may not cover every account.
 */
export function reserveHistoryRows(snapshots: ReserveSnapshot[], symbols: string[]): MultiAreaRow[] {
    return [...snapshots]
        .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
        .map((snapshot) => {
            const totals = new Map<string, BigDecimal>();
            for (const group of GROUPS) {
                for (const holding of snapshot[group] ?? []) {
                    const symbol = holding.asset.trim().toUpperCase();
                    totals.set(
                        symbol,
                        (totals.get(symbol) ?? new BigDecimal("0")).add(new BigDecimal(holding.amountUsd))
                    );
                }
            }
            const row: MultiAreaRow = { t: Date.parse(snapshot.at) };
            for (const symbol of symbols) {
                const total = totals.get(symbol.toUpperCase());
                if (total) row[symbol] = total.toNumber();
            }
            return row;
        });
}
